import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Loader2, Users } from 'lucide-react'
import type { MobilisationTeam } from '../../api'

interface Step1ActivateUsersProps {
    teams: MobilisationTeam[]
    isLoading: boolean
    isActivating: boolean
    onActivateUsers: () => void
    onDeactivateUsers?: () => void
    onToggleTeam?: (teamId: number, isActivated: boolean) => void
}

export function Step1ActivateUsers({
    teams,
    isLoading,
    isActivating,
    onActivateUsers,
    onDeactivateUsers,
    onToggleTeam,
}: Step1ActivateUsersProps) {
    const { t } = useTranslation()

    return (
        <div className="space-y-4">
            <div>
                <h1 className="text-xl font-semibold">{t('recoverRequests.mobilisation.activateUsers', 'Activate Users')}</h1>
                <p className="mt-0.5 text-sm text-muted-foreground">
                    {t('recoverRequests.mobilisation.step1Description', 'Activate dormant recovery user accounts so teams can access the application.')}
                </p>
            </div>
            {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>{t('common.loading')}</span>
                </div>
            ) : (
                <>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-table-header border-b border-table-border">
                                    <TableHead className="font-semibold text-foreground">{t('recoverRequests.mobilisation.team', 'Team')}</TableHead>
                                    <TableHead className="font-semibold text-foreground">{t('recoverRequests.mobilisation.standbyUsers', 'Standby users')}</TableHead>
                                    <TableHead className="font-semibold text-foreground">{t('recoverRequests.mobilisation.standbyUsersActivated', 'Activated')}</TableHead>
                                    <TableHead className="font-semibold text-foreground">{t('recoverRequests.mobilisation.activationStatus', 'Status')}</TableHead>
                                    <TableHead className="font-semibold text-foreground w-[140px] text-right">
                                        {t('recoverRequests.mobilisation.action', 'Action')}
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {teams.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                                            {t('recoverRequests.mobilisation.noTeams', 'No teams found.')}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    teams.map((team) => {
                                        const standby = team.standbyUsers ?? team.userCount
                                        const activated = team.standbyUsersActivated ?? 0
                                        const isActivated = standby > 0 && standby === activated
                                        return (
                                            <TableRow key={team.id}>
                                                <TableCell className="font-medium">{team.teamName}</TableCell>
                                                <TableCell>{standby}</TableCell>
                                                <TableCell>{activated}</TableCell>
                                                <TableCell>
                                                    <Badge variant={isActivated ? 'success' : 'danger'}>
                                                        {isActivated
                                                            ? t('recoverRequests.mobilisation.activated', 'Activated')
                                                            : t('recoverRequests.mobilisation.notActivated', 'Not Activated')}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {onToggleTeam && (standby > 0 && !isActivated ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => onToggleTeam(team.id, false)}
                                                            disabled={isActivating}
                                                        >
                                                            {t('recoverRequests.mobilisation.activate', 'Activate')}
                                                        </Button>
                                                    ) : activated > 0 && isActivated ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => onToggleTeam(team.id, true)}
                                                            disabled={isActivating}
                                                        >
                                                            {t('recoverRequests.mobilisation.deactivate', 'Deactivate')}
                                                        </Button>
                                                    ) : null)}
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex justify-center gap-3">
                        <Button
                            onClick={onActivateUsers}
                            disabled={isActivating || teams.length === 0}
                        >
                            {isActivating ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('recoverRequests.mobilisation.activating', 'Activating...')}
                                </>
                            ) : (
                                <>
                                    <Users className="mr-2 h-4 w-4" />
                                    {t('recoverRequests.mobilisation.activateAllUsers', 'Activate All Users')}
                                </>
                            )}
                        </Button>
                        {onDeactivateUsers && (
                            <Button
                                variant="outline"
                                onClick={onDeactivateUsers}
                                disabled={isActivating || teams.length === 0}
                            >
                                {t('recoverRequests.mobilisation.deactivateAllUsers', 'Deactivate All Users')}
                            </Button>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
